export interface StrategySection {
  title: string;
  icon: string;
  content: string[];
  tips?: string[];
  warning?: string;
}

export interface EventStrategy {
  eventName: string;
  subtitle: string;
  overview: string;
  sections: StrategySection[];
  proTips: string[];
}

// ─── Crystal Clash ───────────────────────────────────────────────────────────

export interface SquadRole {
  role: 'warrior' | 'engineer' | 'all';
  label: string;
  instructions: string[];
}

export interface UnitBlock {
  label: string;         // e.g. "Zones 1–4 | Squads 1–4"
  icon: string;
  accentKey?: 'blue' | 'amber' | 'red' | 'gold';
  instructions: string[];
  roles?: SquadRole[];
  warning?: string;
  note?: string;
}

export interface CrystalClashPlan {
  name: string;          // "Plan A" / "Plan B"
  tagline: string;
  overview: string;
  mapImage?: string;     // path relative to /public
  units: UnitBlock[];
}

export interface CrystalClashStrategy {
  eventName: string;
  subtitle: string;
  commandersNotes: string[];
  plans: CrystalClashPlan[];
}

export const crystalClashData: CrystalClashStrategy = {
  eventName: 'Crystal Clash',
  subtitle: 'Dominance through Precision & Alliance',
  commandersNotes: [
    'The squad leads are the first listed squad members.',
    'Use HELP [building], SWEEP [building], HOLD [building], and BACK [team] consistently. HELP calls one available roamer, not all three.',
    'Check deployed troops before every teleport. A teleport recalls that player’s deployed troops and may weaken garrisons, reinforcements, or collection.',
    'Recover exposed crystals, and start captures only when there is time to complete the useful action.',
  ],
  plans: [
    {
      name: 'Plan A',
      tagline: 'Three Combat Squads · Independent Roaming Support',
      overview: 'Use durable combat squads, Engineers, and independent Warrior roamers. Capture Refinement first for its map-wide capture-time reduction, establish home and away objectives, then rotate around the center unlock and protect crystal collection throughout.',
      mapImage: '/plan-a-new-map.png',
      units: [
        {
          label: 'Opening · Assignments and Refinement',
          icon: '💎',
          accentKey: 'blue',
          instructions: [
            'Squad A: Warriors and Engineers. Capture Refinement first, then transition to the center. Engineers collect around those fights.',
            'Squad B: Warriors and Engineers. Secure the home Ironhold and Prison; defend and recover nearby crystals.',
            'Squad C: Warriors and Engineers. Attempt the away Ironhold opening, then contest support buildings and vulnerable objectives.',
            'Some Warriors operate independently as roamers.',
            'Refinement is the opening priority: its 50% capture-time reduction can turn an approximately two-minute capture into roughly one minute while active.',
          ],
          roles: [
            {
              role: 'all',
              label: 'Roaming Support',
              instructions: [
                'Spread across useful fights. Roamers tackle hard objectives, reinforce critical fights, respond to threatened buildings, and protect successful captures and contested collection.',
                'A HELP [building] call gets support from one available roamer; it does not automatically pull all three.',
                'Squads must remain functional after a roamer leaves. Check the remaining defense first because teleporting recalls that player’s deployed troops and can reduce garrison strength and Warrior stacks.',
              ],
            },
          ],
          note: 'The away Ironhold first-control bonus is valuable, but preserving C’s strong marches matters more than fighting indefinitely.',
        },
        {
          label: 'Center Unlock · Transition Orders',
          icon: '🏰',
          accentKey: 'gold',
          instructions: [
            'As the actual center unlock countdown approaches, Squad A moves to the Central Ironprison. Use the strongest suitable direct attacker; supporting Warriors reinforce the successful occupation.',
            'Squad B remains on the home & away Ironhold. When it is quiet, nearby objectives may use alternate defenders; do not leave the main Ironhold exposed.',
            'Squad C becomes the response team: cover the Refinement handoff and contest important support buildings. Retain the away Ironhold cheaply or revisit it when profitable. Send garrisons to those called out by the Roaming Squad',
            'Engineers can attempt to take support buildings if uncontested. Collecting cystals after a Sronghold capture is the TOP priority though.',
          ],
          warning: 'If a Squad is about to lose a building, engineers must be ready to jump on the expelled crystals.',
        },
        {
          label: 'Center Fight · Score and Support Buildings',
          icon: '⚔️',
          accentKey: 'red',
          instructions: [
            'Center plus the home Ironhold and Prison produces 4,800 base points/minute; the rest of the map produces 5,280/minute. These figures exclude bonuses and temporary-point losses, but show that center needs support-building control and successful collection.',
            'Keep the strongest available garrison leader at center, maintain Warrior stacks, and replace withdrawn or wounded contributions.',
            'Bomber repeatedly affects the central fight; Pillbox improves combat performance. Neither should be ignored because its income is only 320/minute.',
            'If a serious direct attack fails, reassess before repeating. Improve the supporting position or attack a more vulnerable objective while watching for an opening.',
          ],
          note: 'A capture is not operationally complete while valuable crystals remain exposed. Squads should not leave immediately after a capture if its engineers are vulnerable.',
        },
        {
          label: 'Collection, Teleports, and Calls',
          icon: '📣',
          accentKey: 'amber',
          instructions: [
            'Engineers must be ready near an expected ownership change. The strongest suitable attacker handles the objective while other fighters reinforce or protect collectors.',
            'Collect as soon as crystals appear and access is protected. Engineers recover exposed crystals after friendly losses too. Warriors protect collection, then reassess whether to defend or rotate.',
            'Depots can be gathered by engineers during idle time.',
            'Before teleporting, identify deployed troops that will be recalled: a garrison anchor, reinforcements, or active collectors may all be affected. Save paid teleports for a specific outcome: major crystal drop, essential defense rescue, or avoiding forced return to camp.',
            'If an enemy teleports away, inspect their former objective for a capture opening. Pursuing their new location is usually less useful unless it directly advances an objective.',
            'Warriors should use Rapid Recovery after meaningful losses. When practical, use the first worthwhile activation early enough to allow another 30 minutes later. Avoid repeated attacks with strong marches when ordinary healing is ineffective.',
            'HELP [building]: Roamers respond; assigned defenders hold if practical. SWEEP [building]: collectors move in while fighters protect them. HOLD [building]: maintain garrison and nearby protection. BACK [team]: stop attacking and regroup; no extra solo attacks.',
            'In the closing minutes, temporary-point scoring is uncertain. Protect valuable held objectives, recover exposed crystals, and start a new capture only when enough time remains. Avoid pulling a crucial garrison with a last-minute teleport.',
          ],
          note: 'The prearranged center transition and routine crystal collection behavior should happen without lengthy chat messages.',
        },
      ],
    },
    {
      name: 'Plan B',
      tagline: 'Default Formation — 6 Squads · 5 Zones',
      overview: 'Squads 1–5 each hold an assigned zone. Squad 6 operates as a roaming hit squad, reinforcing wherever the battle demands.',
      mapImage: '/plan-a-map.png',
      units: [
        {
          label: 'Zones 1–4 · Squads 1–4',
          icon: '🏰',
          accentKey: 'blue',
          instructions: [
            'Do NOT teleport directly next to a capture point.',
            'A marker will be placed showing where to teleport. If no marker is placed, the entire squad groups up at a location between the two capture points in the zone.',
            'The Squad Commander starts a rally to take and hold each of the 2 capture points in the zone.',
            'Warriors and Engineers join those rallies. No single attacks on capture points unless the Squad Commander gives explicit approval.',
          ],
          roles: [
            {
              role: 'warrior',
              label: 'Warriors',
              instructions: [
                'Protect your squad\'s prisons.',
                'If any squad member is attacked, reinforce and protect each other.',
                'Do not solo attack an enemy prison unless you are certain they are weak.',
              ],
            },
            {
              role: 'engineer',
              label: 'Engineers',
              instructions: [
                'Collect crystals from depots and off the ground.',
                'Stay close to your squad mates at all times.',
              ],
            },
          ],
          note: 'Zones can be quiet. The Legion Commander will rotate squads around the map as needed.',
        },
        {
          label: 'Zone 5 · Squad 5 — Central Prison',
          icon: '🔮',
          accentKey: 'gold',
          instructions: [
            'This is the Central Prison squad. The central prison does not open until Phase 2.',
            'During Phase 1, Squad 5 may assist other zones or help Squad 6 take out enemy prisons.',
            'When Phase 2 begins, the Squad 5 Commander takes Central Prison or starts a rally for it immediately.',
            'All warriors in Squad 5 join the Central Prison rally.',
            'Once Central Prison is under control, send rallies to take the 2 additional control points in the zone.',
            'Stay as a group at all times. Reinforce each other and prioritize Central Prison above everything else.',
          ],
        },
        {
          label: 'Squad 6 — The Hit Squad',
          icon: '⚔️',
          accentKey: 'red',
          instructions: ['Squad 6 is composed entirely of Warriors. You are the roaming strike force.'],
          roles: [
            {
              role: 'all',
              label: 'Priority Order',
              instructions: [
                '1. Assist Squad 5 in taking Central Prison if needed.',
                '2. Assist Zones 1–4 in taking a control point if they are stuck.',
                '3. Go out and destroy. Take out enemy prisons and armies.',
                'Try to stay together whenever possible. The Squad Commander (Spot 1) may split the squad or issue specific orders.',
              ],
            },
          ],
        },
      ],
    },
    {
      name: 'Plan C',
      tagline: 'Contingency — When Central Prison Cannot Be Taken',
      overview:
        'This plan is activated when Central Prison falls out of reach. It must be called EARLY before the enemy accumulates too many control points. We collapse to a tighter formation — 2 squads share zones, and Zone 2 becomes a single focused objective.',
      mapImage: '/plan-b-map.png',
      units: [
        {
          label: 'Zones 1, 3, 4, 5 · Squads 1, 3, 4, 5',
          icon: '🏰',
          accentKey: 'blue',
          instructions: [
            'Collapse to 2 squads per zone.',
            'Each squad takes and holds a single control point in their zone.',
            'All other rules from Plan B remain in effect.',
          ],
        },
        {
          label: 'Zone 2 · Priority Objective',
          icon: '🎯',
          accentKey: 'amber',
          instructions: [
            'Ignore all other buildings. Take this single control point and hold it.',
            'With the 4 control points from the collapsed zones plus this one, we will still out-score the enemy on control points.',
            'All other rules from Plan B remain in effect.',
          ],
        },
        {
          label: 'Squad 6 — The Hit Squad',
          icon: '⚔️',
          accentKey: 'red',
          instructions: [
            'Same as Plan B. Continue roaming, reinforcing, and eliminating enemy forces.',
          ],
        },
      ],
    },
  ],
};

// ─── Placeholder original strategy shape (kept for Sandsea) ─────────────────

export const crystalClashStrategy: EventStrategy = {
  eventName: 'Crystal Clash',
  subtitle: 'Dominance through Precision & Alliance',
  overview: '',
  sections: [],
  proTips: [],
};

// ─── Sandsea Conquest ─────────────────────────────────────────────────────────

export interface SandseaSquadNote {
  squad: string;
  instruction: string;
}

export interface SandseaPhase {
  name: string;
  duration: string;
  icon: string;
  overview: string;
  instructions: string[];
  mapImage?: string;
  squadNotes?: SandseaSquadNote[];
  warning?: string;
  finalPush?: string;
}

export interface SandseaConquestData {
  eventName: string;
  subtitle: string;
  commandersNotes: string[];
  tacticiansNote: string;
  pinnedGuide: string;
  phases: SandseaPhase[];
}

export const sandseaConquestData: SandseaConquestData = {
  eventName: 'Sandsea Conquest',
  subtitle: 'Advance, Anchor & Outlast',
  commandersNotes: [
    'The strategy is valid for all three continents (A, B, and C). If we get Continent A, rotate the lines 90° counter-clockwise. If we get Continent C, rotate the lines 90° clockwise.',
    'Do NOT panic retreat, do NOT move randomly, and stay active until the timer ends.',
    'Winning comes from timing, fuel management, proper queue control, and coordinated use of Tactician skills — not just raw power.',
    'Read all the pinned guides.',
  ],
  tacticiansNote:
    'The Tactician may say something like: "Squad F can teleport to 21. Help 24." This means the Tactician used an ability allowing your squad to teleport marches instantly to building 21. Your squad should send at least 1 march to help. Other squads will NOT be able to teleport this way.',
  pinnedGuide: '/sandsea-guide.jpg',
  phases: [
    {
      name: 'Phase 1',
      duration: '17 Minutes',
      icon: '⚔️',
      overview: 'Advance along your path and lock down the core defense points.',
      mapImage: '/sandsea-phase1.png',
      instructions: [
        'Each squad moves along their assigned path, taking buildings along the way (marked with a circle in your squad\'s color).',
        'Keep moving and push through to the main defense points: 19, 28, 30, 31, 27.',
        'These points must be maintained and kept out of enemy hands.',
        'If your squad\'s main point is secure, you may push forward onto the enemy\'s continent.',
        'Keep an eye on the map at all times.',
        'Do not push too far and over-extend the front.',
        'Back your squad mates up and avoid being cut off from your main defense point.',
      ],
      warning:
        'Communication and coordination are the two most important things in all phases. Look out for Tactician messages and orders.',
    },
    {
      name: 'Phase 2',
      duration: '20 Minutes',
      icon: '🏠',
      overview: 'The lighthouses open. Our goal is to hold 2 lighthouses if possible.',
      mapImage: '/sandsea-phase2.png',
      instructions: [
        'Squad B\'s main focus is to hold our lighthouse.',
        'Squad A and Squad C can assist if possible — either directly at building 24 or by cutting off the enemy.',
        'Squad E can attempt to take the enemy\'s lighthouse with Squad F backing them up.',
        'If Squad D is overrunning the enemy, they can attempt to help as well.',
        'We will NOT attempt to take the furthest lighthouse unless the enemy is extremely weak.',
      ],
      squadNotes: [
        { squad: 'Squad A',   instruction: 'Assist lighthouse at 24 or cut off enemy approach.' },
        { squad: 'Squad B',   instruction: 'Primary holder of our lighthouse. Do not leave it undefended.' },
        { squad: 'Squad C',   instruction: 'Assist lighthouse at 24 or cut off enemy approach.' },
        { squad: 'Squad D',   instruction: 'If overrunning the enemy, assist the lighthouse push.' },
        { squad: 'Squad E',   instruction: 'Attempt to capture the enemy\'s lighthouse.' },
        { squad: 'Squad F',   instruction: 'Back up Squad E on the enemy lighthouse.' },
      ],
      warning:
        'Do not over-extend. Hold as much as you can — but stay ready for Phase 3.',
    },
    {
      name: 'Phase 3',
      duration: '20 Minutes',
      icon: '⛏️',
      overview: 'All focus shifts to the Drilling Platform. Holding it scores nearly as many points as all other buildings combined.',
      mapImage: '/sandsea-phase3.png',
      instructions: [
        'Squad C and Squad D will focus solely on holding the Drilling Platform.',
        'Unless completely overrun, ALL squads (A, B, E, F) must send 1 of their 3 marches to the Platform.',
        'This phase is where we spend the majority of our fuel and abilities.',
      ],
      squadNotes: [
        { squad: 'Squad C',   instruction: 'Dedicated Drilling Platform defenders. Do not leave.' },
        { squad: 'Squad D',   instruction: 'Dedicated Drilling Platform defenders. Do not leave.' },
        { squad: 'Squad A',   instruction: 'Send 1 of 3 marches to the Platform unless completely overrun.' },
        { squad: 'Squad B',   instruction: 'Send 1 of 3 marches to the Platform unless completely overrun.' },
        { squad: 'Squad E',   instruction: 'Send 1 of 3 marches to the Platform unless completely overrun.' },
        { squad: 'Squad F',   instruction: 'Send 1 of 3 marches to the Platform unless completely overrun.' },
      ],
      finalPush:
        'In the final 10 minutes, ignore all low-value buildings. Only focus on: 1. Drilling Platform · 2. Lighthouses.',
    },
  ],
};

// Keep the old shape as an unused stub so existing imports don't break
export const sandseaConquestStrategy: EventStrategy = {
  eventName: 'Sandsea Conquest',
  subtitle: 'Advance, Anchor & Outlast',
  overview: '',
  sections: [],
  proTips: [],
};
